import { Form, Formik, FormikProps } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { staticLoadSelector } from 'slices/static-load/staticLoadSlice';
import { AddDispatchPayload, CreateLanePayload, weekOptions } from 'app/data/static-load/model';
import { useCreateLane } from '../hooks/useLanes';
import { useAddDispatch } from '../hooks/useReviewDispatch';
import { removeEmptyValues, formatTerminalsByAirportCode, formatTerminalsByNumber, formatTerminalsByTurnSegments, parseTurnNumberAirportCodes } from '../utils/utils';
import { useTerminalDetailsByAirportCodes } from '../hooks/useTerminalDetailsByAirportCodes';
import { useTerminalDetailsByNumbers } from '../hooks/useTerminalDetailsByNumbers';
import { getTerminalsList, terminalSummarySelector } from 'slices/terminal-summary/terminalSummarySlice';
import XGSFormInput from 'ui-components/form/input/xgsFormInput';
import XGSFormSelect from 'ui-components/form/select/xgsFormSelect';
import XGSFormTextarea from 'ui-components/form/textarea/xgsFormTextarea';
import { LabelModes } from 'ui-components/molecules/labeled-inputs/labeledInput';
import SlideOutSidebar from 'ui-components/slide-out-sidebar/slideOutSidebar';
import DropsTable, { DropRow } from '../components/dropsTable';
import RouteBuilder, { RouteBuilderDrop } from '../components/routeBuilder';
import { XGSSelectOption } from 'ui-components/xgs-select/xgsSelect';
import Loading from 'ui-components/loading/loading';
import '../cloneWeek.scss';
import Button, { ButtonSizes, ButtonThemes } from 'ui-components/button/button';
import { ReactComponent as EditIcon } from 'images/edit_blue.svg';
import { useMutation } from '@tanstack/react-query';
import { apiQueryAdapter } from 'app/data/apiQueryAdapter';
import { toast } from 'react-toastify';
import { useCarrier } from '../hooks/useCarrier';

type UpdateSidebarProps = {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  data: {
    lane: any;
    drops: any[];
  },
  isLoading: boolean;
  terminalMap: any;
  isEditMode: 'view' | 'edit' | 'create' | 'add-dispatch';
  refetchLane: () => void;
  handleAddDrop: () => void;
  refetchLaneByDay: () => void;
  onSwitchToEdit?: () => void;
}

interface UpdateSidebarFormValues {
  division: string;
  dayOfWeek: string;
  description: string;
  lane: string;
  carrier: string;
  notes: string;
  drops: any[];
  teamRequired: string;
  carrierRateUom: string;
  carrierRate: string;
}

const teamOptions = [
  { label: "Yes", value: "1" },
  { label: "No", value: "0" },
]

const rateUOMOptions = [
  { label: "Miles", value: "MI" },
  { label: "Flat rate", value: "FR" },
]

const formatTeamRequiredDisplay = (value?: string | number) => {
  const normalized = value?.toString();
  if (normalized === "1") return "Yes";
  if (normalized === "0") return "No";
  return normalized || "N/A";
};

const formatCarrierDisplay = (lane?: { carrierName?: string; carrierNumber?: string }) => {
  if (lane?.carrierName && lane?.carrierNumber) {
    return `${lane.carrierName} - ${lane.carrierNumber}`;
  }
  return lane?.carrierNumber ?? "N/A";
};

const formatRateUomDisplay = (value?: string) => {
  if (value === "MI") return "Miles (MI)";
  const option = rateUOMOptions.find((item) => item.value === value);
  return option ? `${option.label} (${option.value})` : value ?? "N/A";
};

const formatCarrierRateDisplay = (value?: string | number) => {
  if (value == null || value === "") return "N/A";

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return value;

  return numericValue.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};


const DEFAULT_CREATE_DROPS: RouteBuilderDrop[] = [
  { id: "create-origin", dropType: "O", terminalNumber: "" },
  { id: "create-destination", dropType: "F", terminalNumber: "" },
];

const UpdateSidebar: React.FC<UpdateSidebarProps> = ({
  isOpen,
  title,
  onClose,
  data,
  isLoading,
  terminalMap,
  isEditMode,
  refetchLane,
  refetchLaneByDay,
  handleAddDrop,
  onSwitchToEdit,
}) => {
  const dispatch = useDispatch();
  const terminalSummaryState = useSelector(terminalSummarySelector);
  const staticLoadState = useSelector(staticLoadSelector);
  const [terminalOptions, setTerminalOptions] = useState<XGSSelectOption[]>([]);

  const { data: carrierData } = useCarrier();
  const createLaneMutation = useCreateLane();
  const addDispatchMutation = useAddDispatch(() => {
    refetchLaneByDay();
    onClose();
  });

  const isFormEditable = isEditMode === 'edit' || isEditMode === 'create' || isEditMode === 'add-dispatch';
  const isCreateLikeMode = isEditMode === 'create' || isEditMode === 'add-dispatch';

  useEffect(() => {
    if (isOpen) {
      dispatch(getTerminalsList(0, null, null, "ACTIVE", "XGS Terminal", true));
    }
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (terminalSummaryState.terminallist) {
      const options = terminalSummaryState.terminallist.map((terminal) => ({
        value: terminal.terminalNumber.toString(),
        label: `${terminal.airportCode} - ${terminal.terminalName.toLowerCase().capitalize()} ${terminal.terminalNumber}`,
      }));
      setTerminalOptions(options);
    }
  }, [terminalSummaryState.terminallist]);

  const updateLaneMutation = useMutation<
    any,
    Error,
    { planId: string; data: any }
  >({
    mutationFn: async ({ planId, data }) => {
      if (!data) throw new Error("Data is required");

      const response = await apiQueryAdapter.query<any[]>(
        `/manifest-management/lanes/${planId}`,
        "PUT",
        { body: data }
      );

      return response.data;
    },
    onSuccess: () => {
      refetchLaneByDay();
      refetchLane();
      toast.info("Lane updated successfully");
    },
  });

  const updateDropMutation = useMutation<
    any,
    Error,
    { dropId: string; data: any }
  >({
    mutationFn: async ({ dropId, data }) => {
      if (!data) throw new Error("Data is required");

      const response = await apiQueryAdapter.query<any[]>(
        `/manifest-management/drops/${dropId}`,
        "PUT",
        { body: data }
      );

      return response.data;
    },
    onSuccess: () => {
      refetchLane();
      toast.info("Drop updated successfully");
    },
  });

  const handleAddDispatch = (values: UpdateSidebarFormValues) => {
    const selectedCarrier = carrierData?.find(
      (carrier: { carrierNumber: string; carrierName?: string }) =>
        carrier.carrierNumber.toString() === values.carrier.toString()
    );

    const originDrop = values.drops?.find((drop: RouteBuilderDrop) => drop.dropType === "O");
    const destinationDrop = values.drops?.find((drop: RouteBuilderDrop) => drop.dropType === "F");

    const payload = removeEmptyValues({
      division: "1",
      weekStartDate: staticLoadState.weekStartDate || data?.lane?.weekStartDate,
      turnNumber: values.lane,
      description: values.description,
      dayOfWeek: parseInt(values.dayOfWeek),
      carrierName: selectedCarrier?.carrierName,
      carrierNumber: values.carrier,
      teamRequired: values.teamRequired === "1",
      manifestType: "L",
      carrierRateUom: values.carrierRateUom,
      carrierRate: values.carrierRate ? Number(values.carrierRate) : undefined,
      originTerminal: originDrop?.terminalNumber ? Number(originDrop.terminalNumber) : undefined,
      destinationTerminal: destinationDrop?.terminalNumber
        ? Number(destinationDrop.terminalNumber)
        : undefined,
      notes: values.notes,
    });

    addDispatchMutation.mutate(payload as AddDispatchPayload);
  };

  const handleSave = async (values: UpdateSidebarFormValues) => {
    try {
      // Update lane first
      await updateLaneMutation.mutateAsync({
        planId: data?.lane?.id!,
        data: {
          division: "1", // Default to Floor Covering
          dayOfWeek: parseInt(values.dayOfWeek),
          turnDescription: values.description,
          turnNumber: values.lane,
          carrierNumber: values.carrier,
          notes: values.notes,
          weekStartDate: data?.lane?.weekStartDate,
          manifestType: 'L', // Default to Linehaul
          carrierRateUom: values.carrierRateUom,
          carrierRate: values.carrierRate,
          drops: values.drops,
          teamRequired: values.teamRequired === "1" ? true : false,
        },
      });

    } catch (err) {
      toast.error("Failed to update lane");
    }
  };

  const handleCreate = (values: UpdateSidebarFormValues) => {
    const selectedCarrier = carrierData?.find(
      (carrier: { carrierNumber: string; carrierName?: string }) =>
        carrier.carrierNumber.toString() === values.carrier.toString()
    );

    const payload = removeEmptyValues({
      division: "1", // Default to Floor Covering
      weekStartDate: staticLoadState.weekStartDate || data?.lane?.weekStartDate,
      turnNumber: values.lane,
      dayOfWeek: parseInt(values.dayOfWeek),
      turnDescription: values.description,
      manifestType: 'L', // Default to Linehaul
      carrierNumber: values.carrier,
      carrierName: selectedCarrier?.carrierName,
      teamRequired: values.teamRequired === "1",
      notes: values.notes,
    });

    createLaneMutation.mutate(payload as CreateLanePayload, {
      onSuccess: () => {
        refetchLaneByDay();
        onClose();
      },
    });
  };


  const handleReorderDrops = async (reorderedDrops: DropRow[]) => {
    try {
      await Promise.all(
        reorderedDrops.map((drop, index) =>
          updateDropMutation.mutateAsync({
            dropId: drop.id,
            data: {
              planId: data?.lane?.id,
              terminalNumber: drop.terminalNumber,
              dropNumber: String(index + 1),
            },
          })
        )
      );
      refetchLane();
    } catch {
      toast.error("Failed to reorder drops");
    }
  };

  const carrierOptions = useMemo(
    () =>
      carrierData?.map((carrier: any) => ({
        label: `${carrier.carrierName} - ${carrier.carrierNumber}`,
        value: carrier.carrierNumber,
      })) ?? [],
    [carrierData]
  );

  const turnNumberAirportCodes = useMemo(
    () => parseTurnNumberAirportCodes(data?.lane?.turnNumber),
    [data?.lane?.turnNumber]
  );

  const terminalNumbersFromDrops = useMemo(
    () =>
      data?.drops
        ?.map((drop) => drop.terminalNumber?.toString())
        .filter((terminalNumber) => terminalNumber != null && terminalNumber !== "") ?? [],
    [data?.drops]
  );

  const { data: terminalDetailsByNumber } = useTerminalDetailsByNumbers(
    isOpen ? terminalNumbersFromDrops : []
  );

  const { data: airportTerminalMatches } = useTerminalDetailsByAirportCodes(
    isOpen ? turnNumberAirportCodes : []
  );

  const mergedTerminalMap = useMemo(
    () => ({
      ...terminalMap,
      ...formatTerminalsByNumber(terminalDetailsByNumber ?? []),
    }),
    [terminalMap, terminalDetailsByNumber]
  );

  const terminalsByAirportCode = useMemo(
    () => ({
      ...formatTerminalsByAirportCode(terminalSummaryState.terminallist ?? []),
      ...formatTerminalsByAirportCode(terminalDetailsByNumber ?? []),
      ...formatTerminalsByTurnSegments(airportTerminalMatches ?? []),
    }),
    [terminalSummaryState.terminallist, terminalDetailsByNumber, airportTerminalMatches]
  );

  const formattedTitleLane = useMemo(() => {
    if (!data?.lane?.turnNumber) return "";

    const turnNumberArray = data.lane.turnNumber.split("-");

    return turnNumberArray.join(" → ");
  }, [data?.lane?.turnNumber]);

  const createModeDrops = useMemo((): RouteBuilderDrop[] => {
    if (!isCreateLikeMode) return DEFAULT_CREATE_DROPS;

    const originLookupKey = (
      data?.lane?.originAirportCode ||
      data?.lane?.originName ||
      ""
    ).toString().toUpperCase();

    let originTerminalNumber = data?.lane?.originTerminalNumber?.toString() || "";

    if (!originTerminalNumber && originLookupKey) {
      const terminalsByCode = formatTerminalsByAirportCode(
        terminalSummaryState.terminallist ?? []
      );
      const matched = terminalsByCode[originLookupKey];
      if (matched?.terminalNumber != null && matched.terminalNumber !== "") {
        originTerminalNumber = matched.terminalNumber.toString();
      }
    }

    return [
      { id: "create-origin", dropType: "O", terminalNumber: originTerminalNumber },
      { id: "create-destination", dropType: "F", terminalNumber: "" },
    ];
  }, [
    isCreateLikeMode,
    data?.lane?.originAirportCode,
    data?.lane?.originName,
    data?.lane?.originTerminalNumber,
    terminalSummaryState.terminallist,
  ]);

  return (
    <SlideOutSidebar
      show={isOpen}
      onClose={onClose}
      header={title}
      size="small-605"
      className="filter-sidebar"
    >

      {isLoading ? (
        <Loading isLoading={true} />
      ) : (
        <Formik
          initialValues={{
            division: data?.lane?.division || "",
            dayOfWeek: data?.lane?.dayOfWeek?.toString() || "",
            description: data?.lane?.turnDescription || "",
            lane: data?.lane?.turnNumber || "",
            carrier: data?.lane?.carrierNumber || "",
            notes: data?.lane?.notes || "",
            drops: isCreateLikeMode
              ? createModeDrops
              : data?.drops?.map((drop) => ({
                id: drop.id,
                dropType: drop.dropType,
                terminalNumber: drop.terminalNumber,
                miles: drop.miles,
                earlyArrivalTime: drop.earlyArrivalTime ?? "",
                earlyDepartureTime: drop.earlyDepartureTime ?? "",
                tractorNumber: drop.tractorNumber ?? "",
                airportCode: drop.airportCode ?? "",
                terminalName: drop.terminalName ?? "",
              })) ?? [],
            teamRequired: data?.lane?.teamRequired || "",
            carrierRateUom: data?.lane?.carrierRateUom || "",
            carrierRate: data?.lane?.carrierRate || "",
          }} onSubmit={(values) => {
            if (isEditMode === 'view') return;
            if (isEditMode === 'edit') {
              handleSave(values);
            } else if (isEditMode === 'create') {
              handleCreate(values);
            } else if (isEditMode === 'add-dispatch') {
              handleAddDispatch(values);
            }
          }}
          enableReinitialize
        >
          {(formikProps: FormikProps<UpdateSidebarFormValues>) => (
            <Form>
              <div className="update-lane_content">
                <div className="update-lane-container-wrapper">
                  <div className='update-lane-title'>{isCreateLikeMode ? 'Route Builder' : `${data?.drops?.length} Drops · ${data?.lane?.miles} mi · ${formattedTitleLane}`}</div>
                  {isEditMode === 'view' && onSwitchToEdit && (
                    <div className="update-lane-actions">
                      <div title="Edit" onClick={onSwitchToEdit}>
                        <div className="clone-week-table-container__actions">
                          <EditIcon className="clone-week-btn__icon" />
                          Edit
                        </div>
                      </div>
                    </div>
                  )}
                  {isFormEditable && <div className='update-lane-actions'>
                    <Button className="update-lane-actions__button" type="button" theme={ButtonThemes.gray} size={ButtonSizes.small} onClick={onClose}>Cancel</Button>
                    <Button
                      className="update-lane-actions__button"
                      type='submit'
                      theme={ButtonThemes.blue}
                      size={ButtonSizes.small}
                      disabled={!formikProps.dirty}
                      spinner={
                        isEditMode === 'create'
                          ? createLaneMutation.isLoading
                          : isEditMode === 'add-dispatch'
                            ? addDispatchMutation.isLoading
                            : updateLaneMutation.isLoading
                      }
                    >
                      {isEditMode === 'add-dispatch' ? 'Add Dispatch' : 'Save Lane'}
                    </Button>
                  </div>}
                </div>
              </div>
              <hr />
              <div className='update-lane-container--general-information-title'>General Information</div>
              <div className="update-lane-container update-lane-container--general-information">
                <div className="update-lane-grid-item">
                  {isFormEditable ? <XGSFormInput
                    type="text"
                    name="lane"
                    required={true}
                    labelMode={LabelModes.column}
                    label="Lane"
                    className="view-lane-section__width-50"
                  /> : <div className="update-lane-item">
                    <div className='update-lane-item_label'>Lane</div>
                    <div className='update-lane-item_value'>{data?.lane?.turnNumber}</div>
                  </div>}
                  {isFormEditable ? <XGSFormInput
                    type="text"
                    name="description"
                    labelMode={LabelModes.column}
                    label="Description"
                    className="view-lane-section__width-50"
                  /> : <div className="update-lane-item">
                    <div className='update-lane-item_label'>Description</div>
                    <div className='update-lane-item_value'>{data?.lane?.turnDescription ?? 'N/A'}</div>
                  </div>}
                  {isFormEditable ? <XGSFormSelect
                    label="Day of Week"
                    name="dayOfWeek"
                    options={weekOptions}
                    readOnly={!isEditMode}
                    value={
                      formikProps.values.dayOfWeek
                        ? weekOptions.find((option) => option.value === formikProps.values.dayOfWeek)
                        : null
                    }
                    onValueChange={(option) => {
                      formikProps.setFieldValue("dayOfWeek", option?.value ?? "");
                    }}
                    formik
                    className="view-lane-section__width-50"
                    labelMode={LabelModes.column}

                  /> : <div className="update-lane-item">
                    <div className='update-lane-item_label'>Day of Week</div>
                    <div className='update-lane-item_value'>{weekOptions.find((option) => option.value === data?.lane?.dayOfWeek?.toString())?.label}</div>
                  </div>}
                  {isFormEditable ? <XGSFormSelect
                    label="Carrier"
                    name="carrier"
                    options={carrierOptions}
                    readOnly={!isEditMode}
                    value={
                      formikProps.values.carrier
                        ? carrierOptions.find((option: { label: string; value: string }) => option.value.toString() === formikProps.values.carrier.toString())
                        : null
                    }
                    onValueChange={(option) => {
                      formikProps.setFieldValue("carrier", option?.value ?? "");
                    }}
                    formik
                    className="view-lane-section__width-50"
                    labelMode={LabelModes.column}
                  />
                    : <div className="update-lane-item">
                      <div className='update-lane-item_label'>Carrier</div>
                      <div className='update-lane-item_value'>{formatCarrierDisplay(data?.lane)}</div>
                    </div>
                  }
                </div>
                <div className="update-lane-grid-item">
                  {isFormEditable ? <XGSFormSelect
                    label="Team"
                    name="teamRequired"
                    options={teamOptions}
                    readOnly={!isEditMode}
                    value={
                      formikProps.values.teamRequired
                        ? teamOptions.find((option: { label: string; value: string }) => option.value.toString() === formikProps.values.teamRequired.toString())
                        : null
                    }
                    onValueChange={(option) => {
                      formikProps.setFieldValue("teamRequired", option?.value ?? "");
                    }}
                    formik
                    className="view-lane-section__width-15"
                    labelMode={LabelModes.column}
                  />
                    : <div className="update-lane-item">
                      <div className='update-lane-item_label'>Team</div>
                      <div className='update-lane-item_value'>{formatTeamRequiredDisplay(data?.lane?.teamRequired)}</div>
                    </div>}
                  
                  {isFormEditable ? <XGSFormSelect
                    label="Rate U/M"
                    name="rateUOM"
                    options={rateUOMOptions}
                    readOnly={!isEditMode}
                    value={
                      formikProps.values.carrierRateUom
                        ? rateUOMOptions.find((option: { label: string; value: string }) => option.value.toString() === formikProps.values.carrierRateUom.toString())
                        : null
                    }
                    onValueChange={(option) => {
                      formikProps.setFieldValue("carrierRateUom", option?.value ?? "");
                    }}
                    formik
                    className="view-lane-section__width-15"
                    labelMode={LabelModes.column}
                  />
                    : <div className="update-lane-item">
                      <div className='update-lane-item_label'>Rate U/M</div>
                      <div className='update-lane-item_value'>{formatRateUomDisplay(data?.lane?.carrierRateUom)}</div>
                    </div>}
                  {isFormEditable ? <XGSFormInput
                    type="text"
                    name="carrierRate"
                    labelMode={LabelModes.column}
                    label="Carrier Rate"
                    className="view-lane-section__width-50"
                  /> : <div className="update-lane-item">
                    <div className='update-lane-item_label'>Carrier Rate</div>
                    <div className='update-lane-item_value'>{formatCarrierRateDisplay(data?.lane?.carrierRate)}</div>
                  </div>}
                </div>
              </div>
              <div style={{ paddingBottom: "14px" }} />
              <div className="update-lane-container--drops">
                <div className="update-lane-container--drops-header">
                  <div className='update-lane-container--drops-title'>{isCreateLikeMode ? 'Route Builder' : 'Stops & Schedule'}</div>
                  {isEditMode === 'edit' && (
                    <button
                      type="button"
                      className="update-lane-container--drops-add"
                      onClick={handleAddDrop}
                    >
                      + Add Drop
                    </button>
                  )}
                </div>
                {isCreateLikeMode ? (
                  <RouteBuilder
                    drops={formikProps.values.drops as RouteBuilderDrop[]}
                    terminalOptions={terminalOptions}
                    onDropsChange={(drops) => formikProps.setFieldValue("drops", drops)}
                  />
                ) : (
                  <DropsTable
                    drops={(formikProps.values.drops as DropRow[]) || []}
                    terminalMap={mergedTerminalMap}
                    turnNumber={data?.lane?.turnNumber}
                    terminalsByAirportCode={terminalsByAirportCode}
                    isEditMode={isEditMode === 'edit'}
                    onSaveDrop={(value, dropId, type) => {
                      const nextDrops = (formikProps.values.drops as DropRow[]).map((drop) =>
                        drop.id === dropId ? { ...drop, [type]: value } : drop
                      );
                      formikProps.setFieldValue("drops", nextDrops);
                    }}
                    onReorder={
                      isEditMode === 'edit'
                        ? (reorderedDrops) => {
                            formikProps.setFieldValue("drops", reorderedDrops);
                            handleReorderDrops(reorderedDrops);
                          }
                        : undefined
                    }
                  />
                )}
              </div>
              <div style={{ paddingBottom: "14px" }} />
              <div>
                {isFormEditable ? <XGSFormTextarea
                  name="notes"
                  label="Notes"
                  required={false}
                /> : <div className="update-lane-item">
                  <div className='update-lane-item_label'>Notes</div>
                  <div className='update-lane-item_value'>{data?.lane?.notes ?? 'N/A'}</div>
                </div>}
              </div>
            </Form>
          )}
        </Formik>
      )
      }

    </SlideOutSidebar >
  );
};

export default UpdateSidebar;
